import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

class ProductRecommendations extends Component {
  /** Whether the section has entered the viewport at least once. */
  #hasIntersected = false;

  /** The recommendation intent used for the currently rendered shelf. */
  #resolvedIntent = null;

  /** Cached cart request used to decide between related and complementary products. */
  #cartRequest = null;

  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#hasIntersected = true;
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #handleCartLinesUpdate = (event) => {
    if (this.dataset.adaptiveIntent !== 'true') return;

    const refresh = () => {
      this.#cartRequest = null;
      if (this.#hasIntersected) this.#refreshRecommendationIntent();
    };

    if (event.promise) {
      event.promise
        .then(({ detail }) => {
          if (!detail?.didError) refresh();
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[product-recommendations] Cart update rejected.', error);
        });
    } else {
      refresh();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  async #loadRecommendations(force = false) {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (!force && recommendationsPerformed === 'true') {
      return;
    }

    try {
      const resolvedIntent = await this.#resolveIntent(productId, intent);
      const result = await this.#fetchCachedRecommendations(productId, sectionId, resolvedIntent);

      if (!result.success) {
        // The Theme Editor can request a section ID that the Section Rendering API cannot resolve.
        if (!Shopify.designMode) this.#handleError(new Error(`Server returned ${result.status}`));
        return;
      }

      if (!result.data?.trim().length) {
        this.#handleError(new Error('No recommendations available'));
        return;
      }

      this.#resolvedIntent = resolvedIntent;
      morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
      this.dataset.recommendationsPerformed = 'true';
      this.dataset.activeIntent = resolvedIntent;
    } catch (error) {
      if (error?.name !== 'AbortError') this.#handleError(error);
    }
  }

  async #resolveIntent(productId, configuredIntent) {
    if (this.dataset.adaptiveIntent !== 'true') return configuredIntent;

    try {
      if (!this.#cartRequest) {
        this.#cartRequest = fetch(this.dataset.cartUrl || `${Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
        }).then((response) => {
          if (!response.ok) throw new Error(`Cart returned ${response.status}`);
          return response.json();
        });
      }
      const cart = await this.#cartRequest;
      const currentProductIsInCart = (cart.items || []).some(
        (item) => String(item.product_id) === String(productId),
      );
      return currentProductIsInCart ? 'complementary' : 'related';
    } catch (error) {
      console.warn('[product-recommendations] Could not resolve cart intent.', error);
      return configuredIntent || 'related';
    }
  }

  async #refreshRecommendationIntent() {
    const { productId, intent } = this.dataset;
    if (!productId) return;

    const nextIntent = await this.#resolveIntent(productId, intent);
    if (nextIntent === this.#resolvedIntent) return;

    this.classList.remove('hidden');
    delete this.dataset.error;
    await this.#loadRecommendations(true);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
